import { useState } from 'react';

interface FaqItemProps {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
}

const FaqItem = ({ q, a, isOpen, onToggle }: FaqItemProps) => (
  <div className="border border-[#E5E5E5] rounded-2xl overflow-hidden bg-white transition-all duration-300">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-[#FF6B35]/5 transition-colors"
    >
      <span className="text-[#0A1628] font-semibold text-sm md:text-base">{q}</span>
      <span className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${isOpen ? 'bg-[#FF6B35] text-white rotate-45' : 'bg-[#F8F9FA] text-[#6C757D]'}`}>
        <i className="ri-add-line text-lg"></i>
      </span>
    </button>
    {isOpen && (
      <div className="px-6 pb-5">
        <div className="h-px bg-[#E5E5E5] mb-4"></div>
        <p className="text-[#6C757D] text-sm leading-relaxed">{a}</p>
      </div>
    )}
  </div>
);

interface FaqListProps {
  faqs: { q: string; a: string }[];
}

const FaqList = ({ faqs }: FaqListProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return (
    <div className="flex flex-col gap-3">
      {faqs.map((faq, idx) => (
        <FaqItem
          key={idx}
          q={faq.q}
          a={faq.a}
          isOpen={openIndex === idx}
          onToggle={() => setOpenIndex(openIndex === idx ? null : idx)}
        />
      ))}
    </div>
  );
};

const processSteps = [
  {
    number: '01',
    icon: 'ri-upload-cloud-2-line',
    title: 'Envías tu diseño',
    desc: 'Mándanos tu arte por WhatsApp o correo en cualquier formato: PNG, PDF, AI, PSD. Si no tienes diseño, nuestro equipo te ayuda a crearlo sin costo adicional.',
    detail: 'Formatos aceptados: PNG · PDF · AI · PSD · SVG',
  },
  {
    number: '02',
    icon: 'ri-printer-line',
    title: 'Lo imprimimos',
    desc: 'Usamos tecnología DTF y sublimación de última generación. Revisamos colores, resolución y calidad antes de imprimir para garantizar un resultado perfecto.',
    detail: 'Tiempo de producción: 24–72 horas hábiles',
  },
  {
    number: '03',
    icon: 'ri-truck-line',
    title: 'Te lo entregamos',
    desc: 'Enviamos a toda Venezuela y hacemos entregas locales en Caracas. También puedes retirar en nuestro local. Empacamos con cuidado para que llegue en perfectas condiciones.',
    detail: 'Envío nacional · Retiro en local · Delivery Caracas',
  },
];

const faqs = [
  {
    q: '¿Cuál es el tiempo de entrega?',
    a: 'El tiempo de producción es de 24 a 72 horas hábiles dependiendo de la cantidad y el tipo de producto. Para pedidos urgentes contáctanos directamente y buscamos la solución más rápida.',
  },
  {
    q: '¿Hay un mínimo de piezas para hacer un pedido?',
    a: 'No. Con la tecnología DTF podemos imprimir desde una sola pieza sin costo adicional. Para pedidos corporativos grandes ofrecemos precios especiales por volumen.',
  },
  {
    q: '¿Qué tipos de impresión ofrecen?',
    a: 'Trabajamos con DTF (Direct to Film), sublimación y bordado computarizado. Cada técnica tiene sus ventajas según el tipo de tela, diseño y cantidad. Te asesoramos para elegir la mejor opción.',
  },
  {
    q: '¿Puedo ver una muestra antes de imprimir todo el pedido?',
    a: 'Sí. Para pedidos grandes enviamos una prueba digital o física antes de proceder con la producción completa, sin costo adicional.',
  },
  {
    q: '¿Qué pasa si no tengo un diseño?',
    a: 'Nuestro equipo de diseño te ayuda a crear tu arte desde cero. Solo cuéntanos tu idea, colores y estilo, y lo desarrollamos para ti incluido en el precio del pedido.',
  },
  {
    q: '¿Hacen envíos fuera de Venezuela?',
    a: 'Por ahora trabajamos principalmente en Venezuela con envíos nacionales. Para pedidos internacionales contáctanos y evaluamos la mejor opción de envío según tu ubicación.',
  },
];

const allProducts = [
  {
    name: 'Camiseta Personalizada',
    category: 'Textiles',
    badge: 'Más Popular',
    desc: 'Impresión DTF de alta calidad en cualquier tela',
    image: 'https://public.readdy.ai/ai/img_res/52978d8903da5545758aed194674dc81.jpg',
  },
  {
    name: 'Camiseta Polo',
    category: 'Textiles',
    badge: '',
    desc: 'Bordado o DTF en polos corporativos y deportivos',
    image: 'https://readdy.ai/api/search-image?query=custom%20polo%20shirt%20with%20embroidered%20logo%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20quality%20corporate%20apparel%2C%20front%20view&width=600&height=600&seq=p002&orientation=squarish',
  },
  {
    name: 'Camiseta Manga Larga',
    category: 'Textiles',
    badge: '',
    desc: 'Diseños completos en manga larga con DTF',
    image: 'https://readdy.ai/api/search-image?query=custom%20long%20sleeve%20shirt%20with%20full%20sleeve%20DTF%20print%20colorful%20design%2C%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%20apparel%2C%20front%20view&width=600&height=600&seq=p011&orientation=squarish',
  },
  {
    name: 'Hoodie Premium',
    category: 'Textiles',
    badge: '',
    desc: 'Sublimación y DTF en hoodies de alta calidad',
    image: 'https://readdy.ai/api/search-image?query=custom%20hoodie%20sweatshirt%20with%20sublimation%20print%20design%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20quality%2C%20front%20view&width=600&height=600&seq=p003&orientation=squarish',
  },
  {
    name: 'Gorra Bordada',
    category: 'Gorras',
    badge: '',
    desc: 'Bordado de precisión en gorras premium',
    image: 'https://readdy.ai/api/search-image?query=custom%20embroidered%20baseball%20cap%20with%20logo%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20headwear%2C%20side%20view&width=600&height=600&seq=p004&orientation=squarish',
  },
  {
    name: 'Gorra Snapback',
    category: 'Gorras',
    badge: '',
    desc: 'Impresión y bordado en gorras snapback',
    image: 'https://readdy.ai/api/search-image?query=custom%20snapback%20cap%20with%20printed%20design%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20streetwear%20headwear%2C%20side%20view&width=600&height=600&seq=p005&orientation=squarish',
  },
  {
    name: 'Taza Sublimada',
    category: 'Drinkware',
    badge: '',
    desc: 'Diseños permanentes en tazas cerámicas',
    image: 'https://readdy.ai/api/search-image?query=custom%20sublimation%20ceramic%20mug%20with%20colorful%20design%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%20drinkware%2C%20front%20view&width=600&height=600&seq=p006&orientation=squarish',
  },
  {
    name: 'Botella Térmica',
    category: 'Drinkware',
    badge: '',
    desc: 'Personalización en acero inoxidable',
    image: 'https://readdy.ai/api/search-image?query=custom%20printed%20stainless%20steel%20thermal%20water%20bottle%20with%20logo%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20drinkware%2C%20front%20view&width=600&height=600&seq=p007&orientation=squarish',
  },
  {
    name: 'Tumbler Personalizado',
    category: 'Drinkware',
    badge: '',
    desc: 'Tumblers con diseños únicos y duraderos',
    image: 'https://readdy.ai/api/search-image?query=custom%20printed%20tumbler%20cup%20with%20colorful%20design%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20drinkware%2C%20front%20view&width=600&height=600&seq=p008&orientation=squarish',
  },
  {
    name: 'Bolsa Tote',
    category: 'Promocionales',
    badge: '',
    desc: 'Bolsas ecológicas con tu marca',
    image: 'https://readdy.ai/api/search-image?query=custom%20printed%20canvas%20tote%20bag%20with%20logo%20design%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20eco%20friendly%20bag%2C%20front%20view&width=600&height=600&seq=p009&orientation=squarish',
  },
  {
    name: 'Artículos de Oficina',
    category: 'Promocionales',
    badge: '',
    desc: 'Kits corporativos con tu branding',
    image: 'https://readdy.ai/api/search-image?query=custom%20branded%20office%20supplies%20pens%20notebooks%20mouse%20pad%20with%20logo%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20corporate%20gifts%2C%20flat%20lay&width=600&height=600&seq=p010&orientation=squarish',
  },
  {
    name: 'Kit Promocional',
    category: 'Promocionales',
    badge: 'Nuevo',
    desc: 'Set completo de artículos personalizados',
    image: 'https://readdy.ai/api/search-image?query=custom%20branded%20promotional%20kit%20set%20with%20various%20items%20mug%20shirt%20cap%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20corporate%20gift%20set%2C%20flat%20lay&width=600&height=600&seq=p012&orientation=squarish',
  },
];

const categories = ['Todos', 'Textiles', 'Gorras', 'Drinkware', 'Promocionales'];

const ProductsPage = () => {
  const [activeCategory, setActiveCategory] = useState('Todos');

  const filtered = activeCategory === 'Todos'
    ? allProducts
    : allProducts.filter((p) => p.category === activeCategory);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-[#0A1628] py-16 md:py-24 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-12 gap-6 p-8">
            {Array.from({ length: 96 }).map((_, i) => (
              <div key={i} className="w-2 h-2 bg-white rounded-full"></div>
            ))}
          </div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8">
          <span className="text-[#FF6B35] text-sm font-semibold uppercase tracking-widest">Catálogo</span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mt-4 mb-6">
            Productos Personalizables
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            Explora nuestro catálogo completo. Cada producto puede ser personalizado con tu marca, logo o diseño único.
          </p>
        </div>
      </section>

      {/* Featured: Camisetas */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-widest">Más Solicitado</span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#0A1628] mt-1">
                Camisetas Personalizadas
              </h2>
              <p className="text-[#6C757D] text-sm mt-1">Impresión DTF de alta resolución en todo tipo de telas</p>
            </div>
            <a
              href="https://wa.me/17542034748"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF6B35] text-white font-semibold px-5 py-2.5 rounded-full text-sm whitespace-nowrap cursor-pointer hover:bg-[#e85a24] transition-colors flex-shrink-0"
            >
              Cotizar Camisetas
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {/* Main featured card */}
            <div className="sm:col-span-1 group rounded-2xl overflow-hidden relative border border-[#E5E5E5]">
              <div className="h-72 sm:h-80 w-full overflow-hidden bg-[#F4F4F4]">
                <img
                  src="https://readdy.ai/api/search-image?query=white%20custom%20t-shirt%20with%20bright%20orange%20graphic%20print%20design%20DTF%20heat%20transfer%20printing%20isolated%20on%20clean%20light%20gray%20background%20professional%20apparel%20product%20photo%20front%20view%20high%20resolution%20vibrant%20colors%20premium%20quality%20cotton%20shirt&width=600&height=640&seq=feat-shirt-main-03&orientation=portrait"
                  alt="Camisetas personalizadas DTF"
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 bg-[#0A1628]">
                <span className="inline-block bg-[#FF6B35] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">Impresión DTF</span>
                <h3 className="text-white font-bold text-lg mb-1">Camiseta Personalizada</h3>
                <p className="text-white/60 text-sm">Colores vibrantes, sin mínimo de piezas</p>
              </div>
            </div>

            {/* Two smaller cards */}
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="group bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#FF6B35]/40 transition-all">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=custom%20polo%20shirt%20with%20embroidered%20company%20logo%20on%20clean%20white%20background%2C%20professional%20corporate%20apparel%2C%20premium%20quality%2C%20front%20view&width=400&height=400&seq=feat-polo-01&orientation=squarish"
                    alt="Camiseta polo personalizada"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <span className="text-[#6C757D] text-xs font-medium">Textiles</span>
                  <h3 className="text-[#0A1628] font-bold text-sm mt-0.5 mb-3">Camiseta Polo</h3>
                  <a href="https://wa.me/17542034748" target="_blank" rel="nofollow noreferrer"
                    className="inline-flex items-center gap-1 text-[#FF6B35] text-xs font-semibold whitespace-nowrap cursor-pointer hover:gap-2 transition-all">
                    Cotizar <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
              <div className="group bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#FF6B35]/40 transition-all">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=custom%20long%20sleeve%20shirt%20with%20colorful%20full%20sleeve%20DTF%20print%20design%2C%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%20apparel&width=400&height=400&seq=feat-longsleeve-01&orientation=squarish"
                    alt="Camiseta manga larga personalizada"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <span className="text-[#6C757D] text-xs font-medium">Textiles</span>
                  <h3 className="text-[#0A1628] font-bold text-sm mt-0.5 mb-3">Manga Larga</h3>
                  <a href="https://wa.me/17542034748" target="_blank" rel="nofollow noreferrer"
                    className="inline-flex items-center gap-1 text-[#FF6B35] text-xs font-semibold whitespace-nowrap cursor-pointer hover:gap-2 transition-all">
                    Cotizar <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
              <div className="group bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#FF6B35]/40 transition-all">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=custom%20hoodie%20sweatshirt%20with%20sublimation%20print%20design%2C%20clean%20white%20background%2C%20professional%20product%20photography%2C%20premium%20quality%20apparel&width=400&height=400&seq=feat-hoodie-01&orientation=squarish"
                    alt="Hoodie personalizado"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <span className="text-[#6C757D] text-xs font-medium">Textiles</span>
                  <h3 className="text-[#0A1628] font-bold text-sm mt-0.5 mb-3">Hoodie Premium</h3>
                  <a href="https://wa.me/17542034748" target="_blank" rel="nofollow noreferrer"
                    className="inline-flex items-center gap-1 text-[#FF6B35] text-xs font-semibold whitespace-nowrap cursor-pointer hover:gap-2 transition-all">
                    Cotizar <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
              <div className="group bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-2xl p-6 flex flex-col justify-center items-center text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl mb-3">
                  <i className="ri-t-shirt-line text-[#FF6B35] text-2xl"></i>
                </div>
                <p className="text-[#0A1628] font-bold text-sm mb-1">¿Diseño propio?</p>
                <p className="text-[#6C757D] text-xs mb-3">Envíanos tu arte y lo imprimimos</p>
                <a href="https://wa.me/17542034748" target="_blank" rel="nofollow noreferrer"
                  className="inline-flex items-center gap-1 bg-[#FF6B35] text-white text-xs font-bold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#e85a24] transition-colors">
                  <i className="ri-whatsapp-line"></i> Enviar diseño
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="sticky top-20 z-40 bg-white border-b border-t border-[#E5E5E5] py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all flex-shrink-0 ${
                  activeCategory === cat
                    ? 'bg-[#FF6B35] text-white'
                    : 'bg-[#F8F9FA] text-[#6C757D] hover:bg-[#E5E5E5]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-14 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop>
            {filtered.map((product) => (
              <div
                key={product.name}
                className="group bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#FF6B35]/40 transition-all duration-300"
              >
                <div className="relative overflow-hidden h-56">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                    <span className="bg-[#0A1628]/80 text-white text-xs font-medium px-3 py-1 rounded-full">
                      {product.category}
                    </span>
                    {product.badge && (
                      <span className="bg-[#FF6B35] text-white text-xs font-bold px-3 py-1 rounded-full">
                        {product.badge}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-[#0A1628] font-bold text-sm mb-1">{product.name}</h3>
                  <p className="text-[#6C757D] text-xs mb-4 leading-relaxed">{product.desc}</p>
                  <a
                    href="https://wa.me/17542034748"
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="flex items-center justify-center gap-2 border-2 border-[#FF6B35] text-[#FF6B35] font-semibold py-2.5 rounded-full text-sm whitespace-nowrap cursor-pointer hover:bg-[#FF6B35] hover:text-white transition-all w-full"
                  >
                    Solicitar Cotización
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso de trabajo */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-widest">Así trabajamos</span>
            <h2 className="text-2xl md:text-4xl font-extrabold text-[#0A1628] mt-3 mb-3">
              De tu idea al producto en 3 pasos
            </h2>
            <p className="text-[#6C757D] text-sm md:text-base max-w-xl mx-auto">
              Proceso simple, rápido y sin complicaciones. Tú nos das el diseño, nosotros hacemos el resto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
            {/* Connector line desktop */}
            <div className="hidden md:block absolute top-12 left-[calc(16.66%+1rem)] right-[calc(16.66%+1rem)] h-px bg-[#E5E5E5] z-0"></div>

            {processSteps.map((step, idx) => (
              <div key={idx} className="relative z-10 flex flex-col items-center text-center group">
                {/* Number + icon */}
                <div className="relative mb-6">
                  <div className="w-24 h-24 flex items-center justify-center bg-[#F8F9FA] rounded-2xl border-2 border-[#E5E5E5] group-hover:border-[#FF6B35]/40 group-hover:bg-[#FF6B35]/5 transition-all duration-300">
                    <i className={`${step.icon} text-3xl text-[#FF6B35]`}></i>
                  </div>
                  <span className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center bg-[#FF6B35] text-white text-xs font-extrabold rounded-full">
                    {step.number}
                  </span>
                </div>
                <h3 className="text-[#0A1628] font-extrabold text-lg mb-3">{step.title}</h3>
                <p className="text-[#6C757D] text-sm leading-relaxed mb-4 max-w-xs">{step.desc}</p>
                <span className="inline-block bg-[#F8F9FA] border border-[#E5E5E5] text-[#6C757D] text-xs font-medium px-4 py-2 rounded-full">
                  {step.detail}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <a
              href="https://wa.me/17542034748"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF6B35] text-white font-bold px-8 py-4 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#e85a24] transition-colors text-sm md:text-base"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Empezar mi pedido ahora
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-widest">FAQ</span>
            <h2 className="text-2xl md:text-4xl font-extrabold text-[#0A1628] mt-3 mb-3">
              Preguntas frecuentes
            </h2>
            <p className="text-[#6C757D] text-sm md:text-base max-w-xl mx-auto">
              Todo lo que necesitas saber antes de hacer tu pedido. ¿No encuentras tu respuesta? Escríbenos.
            </p>
          </div>

          <FaqList faqs={faqs} />

          <div className="mt-10 text-center">
            <p className="text-[#6C757D] text-sm mb-4">¿Tienes otra pregunta?</p>
            <a
              href="https://wa.me/17542034748"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 border-2 border-[#FF6B35] text-[#FF6B35] font-bold px-7 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#FF6B35] hover:text-white transition-all text-sm"
            >
              <i className="ri-question-answer-line"></i>
              Pregúntanos por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#0A1628] text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl md:text-4xl font-extrabold text-white mb-4">¿No encuentras lo que buscas?</h2>
          <p className="text-white/60 text-base md:text-lg mb-8">Contáctanos y personalizamos cualquier producto para tu marca.</p>
          <a
            href="https://wa.me/17542034748"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-8 md:px-10 py-4 md:py-5 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#1ebe5a] transition-colors text-base md:text-lg"
          >
            <i className="ri-whatsapp-line text-xl"></i>
            Hablar por WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
